import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  IconButton,
  Paper,
  Collapse,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import PauseIcon from "@mui/icons-material/Pause";
import InteractiveWorkflow from "./InteractiveWorkflow";
import SECTIONS, { SECTION_MAP } from "./sections";
import { useLearningArticles } from "../../../../hooks/useLearningArticles";
import { useAISupportMessages } from "../../../../hooks/useAISupportMessages";
import firebase from "firebase/compat/app";
import { marked } from "marked";

export default function LearnPage() {
  const { data: articles = [], isLoading, error } = useLearningArticles();
  const [search, setSearch] = useState("");
  const [aiQuery, setAiQuery] = useState("");
  const [showAiAnswer, setShowAiAnswer] = useState(false);
  const [playingArticleKey, setPlayingArticleKey] = useState(null);
  const audioPlayersRef = useRef(new Map());
  const navigate = useNavigate();

  const userId = firebase.auth().currentUser?.uid || "";
  const {
    messages: aiMessages,
    loading: aiLoading,
    sendMessage: aiSendMessage,
  } = useAISupportMessages({ userId });

  const lastAiAnswer = useMemo(() => {
    const assistantMsgs = aiMessages.filter((m) => m.role === "assistant");
    return assistantMsgs.length > 0 ? assistantMsgs[assistantMsgs.length - 1] : null;
  }, [aiMessages]);

  const handleAskAI = useCallback(async () => {
    const q = aiQuery.trim();
    if (!q || aiLoading) return;
    setShowAiAnswer(true);
    setAiQuery("");
    try {
      await aiSendMessage(q);
    } catch {
      // error handled by hook
    }
  }, [aiQuery, aiLoading, aiSendMessage]);

  useEffect(() => {
    return () => {
      audioPlayersRef.current.forEach((audio) => {
        audio.pause();
        audio.src = "";
      });
      audioPlayersRef.current.clear();
    };
  }, []);

  const toggleArticleAudio = useCallback(
    (event, article) => {
      event.preventDefault();
      event.stopPropagation();
      if (!article?.listen) return;

      const articleKey = article.id || article.slug;
      let player = audioPlayersRef.current.get(articleKey);

      if (!player) {
        player = new Audio(article.listen);
        player.preload = "none";
        player.addEventListener("ended", () => {
          setPlayingArticleKey((current) => (current === articleKey ? null : current));
        });
        player.addEventListener("pause", () => {
          setPlayingArticleKey((current) => (current === articleKey ? null : current));
        });
        audioPlayersRef.current.set(articleKey, player);
      }

      if (playingArticleKey === articleKey) {
        player.pause();
        player.currentTime = 0;
        setPlayingArticleKey(null);
        return;
      }

      audioPlayersRef.current.forEach((existingPlayer, key) => {
        if (key !== articleKey) {
          existingPlayer.pause();
          existingPlayer.currentTime = 0;
        }
      });

      player
        .play()
        .then(() => setPlayingArticleKey(articleKey))
        .catch((err) => {
          console.error("Failed to play learning article audio preview:", err);
          setPlayingArticleKey(null);
        });
    },
    [playingArticleKey],
  );

  const renderMarkdown = useCallback((content) => {
    const html = marked.parse(content, { breaks: true, async: false });
    return { __html: html };
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return articles;
    const q = search.toLowerCase();
    return articles.filter(
      (a) =>
        a.title?.toLowerCase().includes(q) ||
        a.excerpt?.toLowerCase().includes(q) ||
        a.tags?.some((t) => t.toLowerCase().includes(q)),
    );
  }, [articles, search]);

  const articlesBySection = useMemo(() => {
    const map = {};
    for (const s of SECTIONS) map[s.id] = [];
    for (const a of filtered) {
      const sid = a.section_id || "getting-started";
      if (!map[sid]) map[sid] = [];
      map[sid].push(a);
    }
    return map;
  }, [filtered]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load Help Center articles. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Help Center
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Everything you need to get started and master Mighty45 coaching.
      </Typography>

      {/* AI Ask Bar */}
      <Paper
        variant="outlined"
        sx={{
          mb: 2.5,
          p: 1.5,
          borderRadius: 2,
          borderColor: "#6366f130",
          bgcolor: "#6366f105",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <AutoAwesomeIcon sx={{ color: "#6366f1", fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#6366f1", whiteSpace: "nowrap" }}>
            Ask AI
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Ask anything about Mighty45... e.g. 'How do I set up meal plans?'"
            value={aiQuery}
            onChange={(e) => setAiQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleAskAI();
              }
            }}
            disabled={aiLoading}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: "0.85rem",
                bgcolor: "white",
              },
            }}
          />
          <IconButton
            onClick={handleAskAI}
            disabled={!aiQuery.trim() || aiLoading}
            size="small"
            sx={{
              bgcolor: aiQuery.trim() ? "#6366f1" : "transparent",
              color: aiQuery.trim() ? "white" : "text.disabled",
              "&:hover": { bgcolor: aiQuery.trim() ? "#4f46e5" : "transparent" },
              width: 36,
              height: 36,
            }}
          >
            {aiLoading ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : (
              <SendIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Stack>

        <Collapse in={showAiAnswer && (!!lastAiAnswer || aiLoading)}>
          <Box sx={{ mt: 1.5, pl: 4 }}>
            {aiLoading && !lastAiAnswer && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
                <CircularProgress size={14} sx={{ color: "#6366f1" }} />
                <Typography variant="caption" color="text.secondary">
                  AI is thinking...
                </Typography>
              </Stack>
            )}
            {lastAiAnswer && (
              <Paper
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "white",
                  border: "1px solid #e5e7eb",
                  position: "relative",
                  "& p": { m: 0, fontSize: "0.8rem", lineHeight: 1.6, "&:not(:last-child)": { mb: 0.75 } },
                  "& ul, & ol": { m: 0, pl: 2.5, fontSize: "0.8rem" },
                  "& li": { mb: 0.25 },
                  "& strong": { fontWeight: 600 },
                  "& h1, & h2, & h3": { fontSize: "0.85rem", fontWeight: 700, mt: 0.5, mb: 0.25 },
                  "& a": { color: "#6366f1" },
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => setShowAiAnswer(false)}
                  sx={{ position: "absolute", top: 4, right: 4, opacity: 0.5 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <Box dangerouslySetInnerHTML={renderMarkdown(lastAiAnswer.content)} />
              </Paper>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Interactive Workflow */}
      <InteractiveWorkflow articles={articles} />

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search articles..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ my: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="disabled" />
            </InputAdornment>
          ),
        }}
      />

      {/* Articles by section */}
      {SECTIONS.map((section) => {
        const sectionArticles = articlesBySection[section.id] || [];
        if (sectionArticles.length === 0 && search.trim()) return null;

        return (
          <Accordion
            key={section.id}
            defaultExpanded
            disableGutters
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "8px !important",
              mb: 1.5,
              "&:before": { display: "none" },
              overflow: "hidden",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {section.label}
                </Typography>
                <Chip
                  label={sectionArticles.length}
                  size="small"
                  sx={{ height: 20, fontSize: "0.7rem" }}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {sectionArticles.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No articles in this section yet.
                </Typography>
              ) : (
                <Grid container spacing={1.5}>
                  {sectionArticles.map((article) => (
                    <Grid item xs={12} sm={6} md={4} key={article.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          "&:hover": { borderColor: "primary.main", boxShadow: 1 },
                          transition: "all 0.15s",
                        }}
                      >
                        <CardActionArea
                          onClick={() => navigate(`/home/learn/${article.slug}`)}
                          sx={{ height: "100%" }}
                        >
                          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600, mb: 0.5, lineHeight: 1.3 }}
                            >
                              {article.title}
                            </Typography>
                            {article.excerpt && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  lineHeight: 1.5,
                                  mb: 1,
                                }}
                              >
                                {article.excerpt}
                              </Typography>
                            )}
                            <Stack direction="row" spacing={1} alignItems="center">
                              {article.read_time_minutes && (
                                <Chip
                                  icon={<AccessTimeIcon sx={{ fontSize: 12 }} />}
                                  label={`${article.read_time_minutes} min`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: "0.65rem" }}
                                />
                              )}
                              {article.workflow_steps?.slice(0, 2).map((s) => (
                                <Chip
                                  key={s}
                                  label={`Step ${s}`}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.65rem",
                                    bgcolor: "action.hover",
                                  }}
                                />
                              ))}
                              {article.listen && (
                                <Tooltip
                                  title={
                                    playingArticleKey === (article.id || article.slug)
                                      ? "Pause audio"
                                      : "Play audio preview"
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    onClick={(e) => toggleArticleAudio(e, article)}
                                    aria-label={
                                      playingArticleKey === (article.id || article.slug)
                                        ? "Pause article audio"
                                        : "Play article audio"
                                    }
                                    sx={{
                                      ml: "auto",
                                      width: 24,
                                      height: 24,
                                      color:
                                        playingArticleKey === (article.id || article.slug)
                                          ? "primary.main"
                                          : "text.secondary",
                                    }}
                                  >
                                    {playingArticleKey === (article.id || article.slug) ? (
                                      <PauseIcon sx={{ fontSize: 14 }} />
                                    ) : (
                                      <VolumeUpIcon sx={{ fontSize: 14 }} />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
